// eslint-disable-next-line import/no-duplicates
// eslint-disable-next-line import/no-duplicates
import { catchError, concatMap, finalize, from, tap } from 'rxjs'
import { computed, useStore } from '@nuxtjs/composition-api'
import useErrorHandler from './useErrorHandler'
import useLocale from './useLocale'

export default function useOauth(context) {
  const store = useStore()
  const { locale } = useLocale()

  const isMobile = computed(() => store.state.isMobile)
  const { errorHandler$, responseCodeToMessage } = useErrorHandler(context)

  function signInWithGoogle(successCallback, errorCallback) {
    if (!process.env.OAUTH_GOOGLE_CLIENT_ID) {
      console.error('Wrong! env variable `OAUTH_GOOGLE_CLIENT_ID` must be set.')
      return
    }
    if (store.state.authModule.user) return

    // eslint-disable-next-line no-undef
    const oauthClient = google.accounts.oauth2.initCodeClient({
      client_id: process.env.OAUTH_GOOGLE_CLIENT_ID,
      scope: 'https://www.googleapis.com/auth/userinfo.email',
      ux_mode: 'popup',
      callback: response => {
        if (store.state.authModule.user) return

        from(store.dispatch('startLoading'))
          .pipe(
            concatMap(() =>
              from(
                store.dispatch('authModule/callbackOauthGoogle', {
                  code: response.code
                })
              )
            ),
            concatMap(data => from(store.dispatch('authModule/oauth', data))),
            tap(({ token, exp, user }) => {
              store.commit('authModule/TOKEN', token)
              store.commit('authModule/TOKEN_EXP', exp)
              store.commit('authModule/USER', user)
              successCallback()
            }),
            catchError(err => {
              errorCallback(err)
              return errorHandler$(err)
            }),
            finalize(() => store.dispatch('completeLoading'))
          )
          .subscribe()
      }
    })
    oauthClient.requestCode()
  }

  function signInWithTelegram(successCallback, errorCallback) {
    if (!process.env.OAUTH_TELEGRAM_BOT_ID) {
      console.error('Wrong! env variable `OAUTH_TELEGRAM_BOT_ID` must be set.')
      return
    }
    if (store.state.authModule.user) return

    const popup = ((url, target, width, height) =>
      window.open(
        url,
        target,
        `width=${width},height=${height},top=${window.screen.height / 2 -
          height / 2},left=${window.screen.width / 2 - width / 2}`
      ))(
      `https://oauth.telegram.org/auth?bot_id=${
        process.env.OAUTH_TELEGRAM_BOT_ID
      }&origin=${encodeURIComponent(window.location.href)}&lang=${
        locale.value
      }`,
      'TelegramLogin',
      550,
      470
    )

    if (!popup) {
      return context.root.$swal({
        type: 'warning',
        title: context.root.$t('common.failOperation'),
        text: 'Please enable popups in your browser settings.'
      })
    }

    window.addEventListener(
      'message',
      function onMessage(event) {
        if (!popup || event.source !== popup) return
        if (store.state.authModule.user) {
          return window.removeEventListener('message', onMessage, false)
        }

        let data = {}
        try {
          data = JSON.parse(event.data)
        } catch (e) {}

        if (data.event === 'auth_result') {
          from(store.dispatch('startLoading'))
            .pipe(
              concatMap(() =>
                from(
                  store.dispatch('authModule/callbackOauthTelegram', {
                    authInfo: data.result
                  })
                )
              ),
              concatMap(data => from(store.dispatch('authModule/oauth', data))),
              tap(({ token, exp, user }) => {
                store.commit('authModule/TOKEN', token)
                store.commit('authModule/TOKEN_EXP', exp)
                store.commit('authModule/USER', user)
                successCallback()
              }),
              catchError(err => {
                errorCallback(err)
                return errorHandler$(err)
              }),
              finalize(() => store.dispatch('completeLoading'))
            )
            .subscribe()
        }
        window.removeEventListener('message', onMessage, false)
      },
      false
    )
    popup.focus()
  }

  function signInWithLine(successCallback, errorCallback) {
    if (!process.env.OAUTH_LINE_ID && !process.env.OAUTH_LINE_SECRET) {
      console.error(
        'Wrong! env variable `OAUTH_LINE_ID` and `OAUTH_LINE_SECRET` must be set.'
      )
    }

    const lineBaseUrl = 'https://access.line.me/oauth2/v2.1/authorize'
    const lineParams = new URLSearchParams({
      response_type: 'code',
      client_id: process.env.OAUTH_LINE_ID,
      redirect_uri: `${window.location.origin}/api/oauth/line`,
      state: 'dev',
      scope: 'profile openid'
    })

    const lineUrl = `${lineBaseUrl}?${lineParams.toString()}`

    const popupWidth = isMobile.value ? screen.width * 0.9 : 600
    const popupHeight = isMobile.value ? screen.height * 0.8 : 775
    const popupLeft = screen.width / 2 - popupWidth / 2
    const popupTop = screen.height / 2 - popupHeight / 2

    const popup = window.open(
      lineUrl,
      'LINE Login',
      `width=${popupWidth},height=${popupHeight},top=${popupTop},left=${popupLeft}`
    )
    if (!popup) {
      return context.root.$swal({
        type: 'warning',
        title: context.root.$t('common.failOperation'),
        text: 'Please enable popups in your browser settings.'
      })
    }

    window.addEventListener('message', function onMessage(event) {
      if (event.origin !== window.location.origin) {
        return console.warn('Invalid origin:', event.origin)
      }
      if (store.state.authModule.user) {
        return window.removeEventListener('message', onMessage, false)
      }
      const { success, message, data } = event.data
      if (success) {
        store.commit('authModule/TOKEN', data.token)
        store.commit('authModule/TOKEN_EXP', data.exp)
        store.commit('authModule/USER', data.user)
        successCallback()
      } else {
        let text = message
        if (data?.code) {
          text = responseCodeToMessage(data.code, data.data)
        }
        context.root
          .$swal({
            type: 'error',
            title: context.root.$t('common.failOperation'),
            text
          })
          .then(() => errorCallback(new Error(text)))
      }
      window.removeEventListener('message', onMessage, false)
    })
  }

  return {
    signInWithGoogle,
    signInWithTelegram,
    signInWithLine
  }
}
